import { useOrganization } from "@clerk/tanstack-react-start"
import { Link, Outlet, createFileRoute, redirect } from "@tanstack/react-router"
import { AppHeader } from "#/components/app-header"
import { useRoles } from "#/hooks/use-roles"
import { cn } from "#/lib/utils"

export const Route = createFileRoute("/_authenticated/admin")({
	beforeLoad: ({ context }) => {
		const roles = (context as { roles?: string[] }).roles ?? []
		// Both super admins and platform operators can enter /admin/*
		if (!roles.includes("platform_admin") && !roles.includes("platform_operator")) {
			throw redirect({ to: "/" })
		}
	},
	component: AdminLayout,
})

type AdminTab = { label: string; to: string; superAdminOnly?: boolean }

const tabs: readonly AdminTab[] = [
	{ label: "Dashboard", to: "/admin" },
	{ label: "Tenants", to: "/admin/tenants" },
	{ label: "Staff", to: "/admin/staff", superAdminOnly: true },
	{ label: "Demo Sessions", to: "/admin/demo-sessions", superAdminOnly: true },
	{ label: "Feature Flags", to: "/admin/feature-flags", superAdminOnly: true },
	{ label: "Field Library", to: "/admin/field-library", superAdminOnly: true },
] as const

const linkClass =
	"rounded-md px-2 py-2 text-xs text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors whitespace-nowrap md:px-3 md:text-sm"
const activeLinkClass = cn(linkClass, "bg-accent text-foreground")

function AdminLayout() {
	const { organization } = useOrganization()
	const { isPlatformAdmin } = useRoles()
	const visibleTabs = tabs.filter((t) => !t.superAdminOnly || isPlatformAdmin)
	return (
		<div className="flex min-h-screen flex-col bg-background">
			<AppHeader currentLog={null} accentColor="#7a8c9e" orgName={organization?.name} />
			<div className="mx-auto w-full max-w-6xl space-y-6 p-6">
				<div>
					<h1>Platform Administration</h1>
					<p className="text-sm text-foreground-secondary">
						Manage tenants, entitlements, and platform settings
					</p>
				</div>

				<nav className="flex flex-nowrap gap-1 overflow-x-auto border-b border-border pb-px">
					{visibleTabs.map((tab) => (
						<Link
							key={tab.to}
							to={tab.to}
							className={linkClass}
							activeOptions={{ exact: tab.to === "/admin" }}
							activeProps={{ className: activeLinkClass }}
						>
							{tab.label}
						</Link>
					))}
				</nav>

				<Outlet />
			</div>
		</div>
	)
}
